import React, { ForwardRefExoticComponent, PropsWithoutRef, RefAttributes } from 'react'
import styled from 'styled-components'
import { giphyDarkestGrey, giphyBlack } from '@giphy/js-brand'
import type { Side } from '@floating-ui/react-dom-interactions'

import type { GifPopoverContext } from './types'

export type PointerProps = {
    borderRadius?: number
    context: GifPopoverContext
    height: number
    width: number
} & React.ComponentPropsWithRef<'div'>

type CornerProps = {
    $offset: number
    $size: number
}

function getXCornerPosition(cornerProps: CornerProps) {
    const { $size } = cornerProps
    return `-${$size}px`
}

function getYCornerPosition(cornerProps: CornerProps) {
    const { $offset } = cornerProps
    return `${$offset}px`
}

const BasePointerContainer = styled.div<{
    $borderRadius: number
    $height: number
    $offset: number
    $side: Side
    $width: number
}>`
    background: ${({ $offset, $side }) =>
        $side === 'top'
            ? `linear-gradient(-180deg, ${giphyDarkestGrey} 0, ${giphyDarkestGrey} ${$offset}px, ${giphyDarkestGrey} ${$offset}px, ${giphyBlack} 100%)`
            : `linear-gradient(-180deg, ${giphyBlack} 0, ${giphyDarkestGrey} calc(100% - ${$offset}px), ${giphyDarkestGrey} calc(100% - ${$offset}px), ${giphyDarkestGrey} 100%)`};
    height: ${({ $height }) => `${$height}px`};
    margin-top: ${({ $side, $offset }) => ($side === 'top' ? 0 : `${$offset}px`)};
    margin-bottom: ${({ $side, $offset }) => ($side === 'top' ? `${$offset}px` : 0)};
    pointer-events: none;
    position: absolute;
    width: ${({ $width }) => `${$width}px`};
`

const TopPointerContainer = styled(BasePointerContainer)`
    border-radius: ${({ $borderRadius }) => `0 0 ${$borderRadius}px ${$borderRadius}px `};
    bottom: ${({ $height }) => `-${$height}px`};
`

const BottomPointerContainer = styled(BasePointerContainer)`
    border-radius: ${({ $borderRadius }) => `${$borderRadius}px ${$borderRadius}px 0 0`};
    top: ${({ $height }) => `-${$height}px`};
`

const BaseCorner = styled.div<CornerProps>`
    height: ${({ $size }) => `${$size}px`};
    overflow: hidden;
    position: absolute;
    width: ${({ $size }) => `${$size}px`};

    &:before {
        border-radius: 50%;
        content: '';
        display: block;
        height: 200%;
        position: absolute;
        width: 200%;
    }
`

const TopLeftCorner = styled(BaseCorner)`
    left: ${getXCornerPosition};
    top: ${getYCornerPosition};

    &:before {
        box-shadow: ${({ $size }) => `${$size / 2}px -${$size / 2}px 0 0 ${giphyDarkestGrey}`};
        right: 0;
        top: 0;
    }
`

const TopRightCorner = styled(BaseCorner)`
    right: ${getXCornerPosition};
    top: ${getYCornerPosition};

    &:before {
        box-shadow: ${({ $size }) => `-${$size / 2}px -${$size / 2}px 0 0 ${giphyDarkestGrey}`};
        left: 0;
        top: 0;
    }
`

const BottomLeftCorner = styled(BaseCorner)`
    left: ${getXCornerPosition};
    bottom: ${getYCornerPosition};

    &:before {
        box-shadow: ${({ $size }) => `${$size / 2}px ${$size / 2}px 0 0 ${giphyDarkestGrey}`};
        bottom: 0;
        right: 0;
    }
`

const BottomRightCorner = styled(BaseCorner)`
    right: ${getXCornerPosition};
    bottom: ${getYCornerPosition};

    &:before {
        box-shadow: ${({ $size }) => `-${$size / 2}px ${$size / 2}px 0 0 ${giphyDarkestGrey}`};
        bottom: 0;
        left: 0;
    }
`

export const Pointer = React.forwardRef<HTMLDivElement, PointerProps>((props: PointerProps, ref) => {
    const { context, className, borderRadius = 16, height, width, style, ...other } = props
    const { floatingRect, side, middlewareData } = context

    const Container = side === 'top' ? TopPointerContainer : BottomPointerContainer
    const LeftCorner = side === 'top' ? TopLeftCorner : BottomLeftCorner
    const RightCorner = side === 'top' ? TopRightCorner : BottomRightCorner

    const offset = borderRadius
    const x = middlewareData?.arrow?.x
    const y = middlewareData?.arrow?.y

    /*
     * Check if the pointer is shifted to the left edge of the viewport
     * If the pointer is shifted to the left, we don't have enough space to display the left corner and would like to hide it
     * x may be null or undefined. In this case, consider that the pointer isn't shifted to the edge to avoid flickering
     */
    const leftEdge = x != null ? x - borderRadius <= 0 : false
    /*
     * Check if the pointer is shifted to the right edge of the viewport
     * If the pointer is shifted to the right, we don't have enough space to display the right corner and would like to hide it
     * x may be null or undefined. In this case, consider that the pointer isn't shifted to the edge to avoid flickering
     */
    const rightEdge = x != null ? x + width + borderRadius >= floatingRect.width : false
    /*
     * If the pointer shifts to an edge, we are hiding a corner.
     * Move the pointer slightly toward the missing corner to make the shape smoother.
     */
    let shiftX = 0
    shiftX = leftEdge ? -borderRadius / 8 : shiftX
    shiftX = rightEdge ? borderRadius / 8 : shiftX

    return (
        <Container
            ref={ref}
            $borderRadius={borderRadius}
            $height={height + offset}
            $offset={offset}
            $side={side}
            $width={width}
            className={[Pointer.className, className].join(' ')}
            style={{
                left: x != null ? `${x + shiftX}px` : '',
                top: y != null ? `${y}px` : '',
                ...style,
            }}
            {...other}
        >
            {!leftEdge && <LeftCorner $offset={offset} $size={borderRadius} />}
            {!rightEdge && <RightCorner $offset={offset} $size={borderRadius} />}
        </Container>
    )
}) as ForwardRefExoticComponent<PropsWithoutRef<PointerProps> & RefAttributes<HTMLDivElement>> & {
    className: string
}

Pointer.className = 'giphy-popover-pointer'
